import React from 'react';
import useCurrentTextStyle from '../../../../hooks/useCurrentTextStyle';
import { useResponsiveBreakpoints } from '../../../../hooks/useMediaQuery';

interface MinicartHeaderProps {
  item_count: number;
  block?: any;
  settings: {
    // Content settings
    title_text?: string;
    title_text_style?: string;
    title_text_style_enable_responsive?: boolean;
    title_text_style_mobile?: string;
    title_text_style_tablet?: string;
    title_text_style_ultrawide?: string;
    title_text_color?: string;
    show_cart_item_count?: boolean;
    item_count_text_style?: string;
    item_count_text_style_enable_responsive?: boolean;
    item_count_text_style_mobile?: string;
    item_count_text_style_tablet?: string;
    item_count_text_style_ultrawide?: string;
    item_count_text_color?: string;
    close_icon?: string;
    close_icon_size?: number;

    // Appearance settings
    color_schema?: string;
    border_radius?: number;

    // Layout & Spacing settings
    direction?: string;
    header_alignment?: 'start' | 'center' | 'end';
    padding_top?: number;
    padding_top_enable_responsive?: boolean;
    padding_top_mobile?: number;
    padding_top_tablet?: number;
    padding_top_ultrawide?: number;
    padding_bottom?: number;
    padding_bottom_enable_responsive?: boolean;
    padding_bottom_mobile?: number;
    padding_bottom_tablet?: number;
    padding_bottom_ultrawide?: number;
    padding_left_right?: number;
    padding_left_right_enable_responsive?: boolean;
    padding_left_right_mobile?: number;
    padding_left_right_tablet?: number;
    padding_left_right_ultrawide?: number;
    margin_top?: number;
    margin_bottom?: number;
    margin_left_right?: number;

    // Divider settings
    show_divider?: boolean;
    divider_color?: string;
    divider_thickness?: number;
  };

  onClose: () => void;
}

export const MinicartHeader = ({ item_count = 0, settings = {}, block, onClose }: MinicartHeaderProps) => {
  const { getCurrentTextStyle } = useCurrentTextStyle();
  const { isMobile, isTablet, isUltrawide } = useResponsiveBreakpoints();

  // Helper function to get responsive padding values
  const getResponsivePadding = (
    basePadding: number,
    enableResponsive: boolean,
    mobilePadding?: number,
    tabletPadding?: number,
    ultrawidePadding?: number
  ) => {
    if (!enableResponsive) return basePadding;

    if (isMobile && mobilePadding !== undefined) return mobilePadding;
    if (isTablet && tabletPadding !== undefined) return tabletPadding;
    if (isUltrawide && ultrawidePadding !== undefined) return ultrawidePadding;

    return basePadding;
  };

  // Content settings with responsive support
  const title_text = settings?.title_text || 'Cart';
  const title_text_style = getCurrentTextStyle(
    settings?.title_text_style,
    settings?.title_text_style_enable_responsive,
    settings?.title_text_style_mobile,
    settings?.title_text_style_tablet,
    settings?.title_text_style_ultrawide,
  );
  const title_text_color = settings?.title_text_color;
  const show_cart_item_count = settings?.show_cart_item_count ?? true;
  const item_count_text_style = getCurrentTextStyle(
    settings?.item_count_text_style,
    settings?.item_count_text_style_enable_responsive,
    settings?.item_count_text_style_mobile,
    settings?.item_count_text_style_tablet,
    settings?.item_count_text_style_ultrawide
  );
  const item_count_text_color = settings?.item_count_text_color;
  const close_icon = settings?.close_icon || 'icon-x-thin';
  const close_icon_size = settings?.close_icon_size || 24;

  // Layout & Spacing with responsive support
  const direction = settings?.direction || 'row';
  const header_alignment = settings?.header_alignment || 'center';
  
  // Calculate responsive padding values
  const paddingTop = getResponsivePadding(
    settings?.padding_top ?? 10,
    settings?.padding_top_enable_responsive ?? false,
    settings?.padding_top_mobile,
    settings?.padding_top_tablet,
    settings?.padding_top_ultrawide
  );
  
  const paddingBottom = getResponsivePadding(
    settings?.padding_bottom ?? 10,
    settings?.padding_bottom_enable_responsive ?? false,
    settings?.padding_bottom_mobile,
    settings?.padding_bottom_tablet,
    settings?.padding_bottom_ultrawide
  );
  
  const paddingLeftRight = getResponsivePadding(
    settings?.padding_left_right ?? 10,
    settings?.padding_left_right_enable_responsive ?? false,
    settings?.padding_left_right_mobile,
    settings?.padding_left_right_tablet,
    settings?.padding_left_right_ultrawide
  );
  
  const marginTop = settings?.margin_top ?? 0;
  const marginBottom = settings?.margin_bottom ?? 0;
  const marginLeftRight = settings?.margin_left_right ?? 0;

  // Appearance
  const borderRadius = settings?.border_radius || 0;

  // Divider settings
  const show_divider = settings?.show_divider || false;
  const divider_color = settings?.divider_color || '#e5e7eb';
  const divider_thickness = settings?.divider_thickness || 1;

  const containerStyle = {
    marginTop: `${marginTop}px`,
    marginBottom: `${marginBottom}px`,
    marginLeft: `${marginLeftRight}px`,
    marginRight: `${marginLeftRight}px`,
    paddingTop: `${paddingTop}px`,
    paddingBottom: `${paddingBottom}px`,
    paddingLeft: `${paddingLeftRight}px`,
    paddingRight: `${paddingLeftRight}px`,
    display: 'flex',
    flexDirection: direction === 'row' ? 'row' : 'column',
    justifyContent: direction === 'row' ? 'space-between' : 'flex-start',
    alignItems: header_alignment === 'center' ? 'center' : header_alignment === 'end' ? 'flex-end' : 'flex-start',
    // gap: direction === 'column' ? '20px' : '0',
    borderRadius: `${borderRadius}px`
  } as React.CSSProperties;

  // Get the correct color schema from block data
  const colorSchema = block?.color_schema || settings?.color_schema;

  return (
    <>
      <button
        className={`icon icon-${close_icon} cursor-pointer hover:opacity-70 transition-opacity absolute top-3 md:top-4 right-4`}
        aria-label='Close minicart'
        onClick={onClose}
        style={{ fontSize: close_icon_size, width: close_icon_size, height: close_icon_size }}
      ></button>
      <div
        className={`minicart-header ${colorSchema ? `color-${colorSchema}` : ''}`}
        style={containerStyle}
      >
        <div className={`flex w-full`}>
          <button
            className={`hidden md:flex items-center gap-1 cursor-pointer hover:opacity-70 transition-opacity mb-5`}
            aria-label='Close minicart'
            onClick={onClose}
          >
            <span className="icon icon-arrow-back"></span>
            <span className="link underline">Continue Shopping</span>
          </button>
        </div>
        <div className='minicart-header__title flex items-center gap-2 w-full mb-2 md:mb-5'>
          <div className={`flex w-full justify-between`}>
            <div className={`flex  justify-center items-center gap-[16px]`}>
              <h2
                className={title_text_style}
                style={{ color: title_text_color }}
              >
                {title_text}
              </h2>
              {show_cart_item_count && (
                <span className={`cart-item-count ${item_count_text_style}`}>
                  <span
                    className='cart-item-count__value'
                    data-cart-item-count
                    style={{ color: item_count_text_color }}
                  >
                    {item_count} items
                  </span>
                </span>
              )}
            </div>
          </div>
        </div>
        {show_divider && (
          <div
            style={{
              width: '100%',
              height: divider_thickness,
              backgroundColor: divider_color
            }}
          />
        )}
      </div>
    </>
  );
};
